/**
 * Futures Module
 * Provides data access interfaces for futures
 */

import { Client, ClientOptions } from '../../utils/client';
import { convertKlinePeriod } from '../../utils';
import { APIResponse } from '../../types'
import { TickData, TickDataMap } from '../../types/tick';
import { QuoteData, QuoteDataMap } from '../../types/quote';
import { DepthData, DepthDataMap } from '../../types/depth';
import { KlineData, KlineDataMap, GetKlineOptions, GetKlinesOptions } from '../../types/kline';
import { CreateSocketOptions } from '../../types/socket';
import SocketClient from '../../utils/SocketClient';

/**
 * Futures SDK Client
 * Specialized for futures data access
 */
export class FutureClient extends Client {
    constructor(token: string, options?: ClientOptions) {
        super(token, options);
    }
    /**
     * Get Futures Real-Time Trade Snapshot
     * @description Query latest trade real-time market data for futures based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-tick Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., CL, GC etc.)
     * @returns Promise wrapped standard API response, data is real-time market data {@link TickData}
     * @example
     * ```typescript
     * // Get real-time trade market data for Crude Oil futures (CL)
     * getTick({ region: 'US', code: 'CL' }).then(response => {
     *     console.log('Real-time market:', response.data);
     *   }).catch(err => {
     *     console.error('Failed to get market data:', err);
     *   });
     * ```
     */
    async getTick(params: { region: string, code: string }): Promise<APIResponse<TickData>> {
        const { region, code } = params;
        return this._get('/future/tick', { region, code });
    }

    /**
     * Get Futures Latest Quote
     * @description Query latest quote real-time market data for futures based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-quote Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is latest quote data {@link QuoteData}
     * @example
     * ```typescript
     * // Get latest quote for Crude Oil futures (CL)
     * getQuote({ region: 'US', code: 'CL' }).then(response => {
     *    console.log('Latest quote:', response.data);
     * }).catch(err => {
     *   console.error('Failed to get quote:', err);
     * });
     * ```
     */
    async getQuote(params: { region: string, code: string }): Promise<APIResponse<QuoteData>> {
        const { region, code } = params;
        return this._get('/future/quote', { region, code });
    }

    /**
     * Get Futures Latest Order Book
     * @description Query latest order book data for futures based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-depth Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is latest order book data {@link DepthData}
     * @example
     * ```typescript
     * // Get latest order book for Crude Oil futures (CL)
     * getDepth({ region: 'US', code: 'CL' }).then(response => {
     *   console.log('Latest order book:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get order book:', err);
     * });
     * ```
     */
    async getDepth(params: { region: string, code: string }): Promise<APIResponse<DepthData>> {
        const { region, code } = params;
        return this._get('/future/depth', { region, code });
    }

    /**
     * Get Futures K-Line Data
     * @description Query futures K-line data based on market code, symbol code, K-line period, end time and number of records to return
     * @param options - Query parameters object {@link GetKlineOptions}
     * @param options.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-kline Official Documentation} for complete supported list
     * @param options.code - Symbol code (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is K-line data array {@link KlineData}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for Crude Oil futures (CL)
     * getKline({ region: 'US', code: 'CL', interval: '5m', limit: 100 }).then(response => {
     *   console.log('K-line data:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKline(options: GetKlineOptions): Promise<APIResponse<KlineData[]>> {
        const { region, code, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, code, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/future/kline', params);
    }

    /**
     * Get Multiple Futures Latest Trades
     * @description Query latest trade real-time market data for multiple futures based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-ticks Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is multiple futures latest trade data object {@link TickDataMap}
     * @example
     * ```typescript
     * // Get latest trade market data for Crude Oil (CL) and Gold (GC) futures
     * getTicks({ region: 'US', codes: ['CL', 'GC'] }).then(response => {
     *  console.log('Latest trades:', response.data);
     * }).catch(err => {
     * console.error('Failed to get trade market data:', err);
     * });
     * ```
     */
    async getTicks(params: { region: string, codes: string[] | string }): Promise<APIResponse<TickDataMap>> {
        const { region, codes } = params;
        return this._get('/future/ticks', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Futures Latest Quotes
     * @description Query latest quote real-time market data for multiple futures based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-quotes Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is multiple futures latest quote data object {@link QuoteDataMap}
     * @example
     * ```typescript
     * // Get latest quotes for Crude Oil (CL) and Gold (GC) futures
     * getQuotes({ region: 'US', codes: ['CL', 'GC'] }).then(response => {
     * console.log('Latest quotes:', response.data);
     * }).catch(err => {
     * console.error('Failed to get quotes:', err);
     * });
     * ```
     */
    async getQuotes(params: { region: string, codes: string[] | string }): Promise<APIResponse<QuoteDataMap>> {
        const { region, codes } = params;
        return this._get('/future/quotes', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Futures Latest Order Books
     * @description Query latest order book real-time market data for multiple futures based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-depths Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is multiple futures latest order book data object {@link DepthDataMap}
     * @example
     * ```typescript
     * // Get latest order books for Crude Oil (CL) and Gold (GC) futures
     * getDepths({ region: 'US', codes: ['CL', 'GC'] }).then(response => {
     * console.log('Latest order books:', response.data);
     * }).catch(err => {
     * console.error('Failed to get order books:', err);
     * });
     * ```
     */
    async getDepths(params: { region: string, codes: string[] | string }): Promise<APIResponse<DepthDataMap>> {
        const { region, codes } = params;
        return this._get('/future/depths', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Futures K-Line Data
     * @description Query K-line data for multiple futures based on market code, symbol code list, K-line period, end time and number of records to return
     * @param options - Query parameters object {@link GetKlinesOptions}
     * @param options.region - Market code (e.g., US, CN, HK etc.), see {@link https://docs.itick.org/rest-api/future/future-klines Official Documentation} for complete supported list
     * @param options.codes - Symbol code list (e.g., CL, GC etc.)
     * @returns Promise wrapped API response, data is multiple futures K-line data object {@link KlineDataMap}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for Crude Oil (CL) and Gold (GC) futures
     * getKlines({ region: 'US', codes: ['CL', 'GC'], interval: '5m', limit: 100 }).then(response => {
     *  console.log('K-line data:', response.data);
     * }).catch(err => {
     * console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKlines(options: GetKlinesOptions): Promise<APIResponse<KlineDataMap>> {
        const { region, codes, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, codes: Array.isArray(codes) ? codes.join(',') : codes, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/future/klines', params);
    }
    /**
     * Create WebSocket Connection
     * @description Creates a WebSocket connection and returns a SocketClient object for managing connection and subscribing to real-time data. Subscription data can be specified through options parameter.
     * Automatically handles connection retry and heartbeat mechanism to ensure connection stability and reliability.
     * @param options - Optional options object for creating WebSocket connection {@link CreateSocketOptions}
     * @returns SocketClient object for managing WebSocket connection
     * @example
     * ```typescript
     * // Create a WebSocket connection and subscribe to 5-minute K-line and real-time quote data for Crude Oil (CL) and Gold (GC) futures
     * const client = new FutureClient(token);
     * const socket = client.createSocket({
     *  subscribeData: {
     *    codes: ['CL$US', 'GC$US'],
     *    types: ['kline@5m', 'quote']
     *  }
     * });
     * socket.onSocketMessage(data => {  
     *  console.log('Received WebSocket message:', data);
     * });
     * ```
     * @see Official Documentation https://docs.itick.org/websocket/future
     */
    createSocket(options?: CreateSocketOptions): SocketClient {
        return this._createSocket('/future', options);
    }
}
